import { Button, useDisclosure } from '@heroui/react';
import { DateTime } from 'luxon';
import React from 'react';
import { FiInfo } from 'react-icons/fi';

import { BaseModal } from '../../../../../components/BaseModal';
import { TransmissionEventRowProps } from '../types';

import { MetaDataTable } from './MetaDataTable';
import { NewStateIndicator } from './NewStateIndicator';

const getTimestampDelta = (
  timestamp: string,
  orderCreatedAt?: number
): string => {
  if (!orderCreatedAt) return '';

  const eventTime = DateTime.fromISO(timestamp);
  const orderTime = DateTime.fromMillis(orderCreatedAt);
  const diff = eventTime.diff(orderTime, ['hours', 'minutes', 'seconds']);

  return `+ ${diff.toFormat('hh:mm:ss')}`;
};

export const TransmissionEventRow: React.FC<TransmissionEventRowProps> = ({
  transmissionEvent,
  orderCreatedAt,
}) => {
  const { timestamp, metaData, newState } = transmissionEvent;
  const formattedTimestamp = DateTime.fromISO(timestamp).toFormat(
    'yyyy-MM-dd HH:mm:ss'
  );
  const timestampDelta = getTimestampDelta(timestamp, orderCreatedAt);
  const {
    isOpen: isMetadataOpen,
    onOpen: openMetadata,
    onClose: closeMetadata,
  } = useDisclosure();

  return (
    <div className="flex items-center gap-4 text-sm">
      {/* State Indicator */}
      <div className="w-32 flex-shrink-0">
        <NewStateIndicator transmissionEvent={transmissionEvent} />
      </div>

      {/* Timestamp */}
      <span className="text-foreground font-mono">{formattedTimestamp}</span>

      {/* Delta */}
      {timestampDelta && (
        <span className="text-default-500 font-mono">{timestampDelta}</span>
      )}

      {/* Metadata viewer */}
      <Button
        isIconOnly
        size="sm"
        variant="light"
        aria-label="View metadata"
        onPress={openMetadata}
        className="text-default-400 hover:text-default-600 min-w-8"
      >
        <FiInfo className="w-4 h-4" />
      </Button>

      <BaseModal
        isOpen={isMetadataOpen}
        onClose={closeMetadata}
        size="2xl"
        scrollBehavior="inside"
        title="Transmission Event Metadata"
        subtitle={`${newState} · ${formattedTimestamp}`}
        footerContent={{
          onCancel: closeMetadata,
          cancelText: 'Close',
        }}
      >
        <MetaDataTable metaData={metaData ?? null} />
      </BaseModal>
    </div>
  );
};
